// =============================================================================
// Appointment Notification Service
// src/app/core/services/appointment-notification.service.ts
// =============================================================================

import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, interval, switchMap, tap, catchError, of, map } from 'rxjs';
import {
  AppointmentNotificationResponse,
  NotificationSummaryResponse,
  NotificationPageResponse
} from '../models/appointment-notification.model';
import { environment } from '../../../../environments/environment';
import { ApiResponse } from '../../../core/models/api-response.model';

@Injectable({
  providedIn: 'root'
})
export class AppointmentNotificationService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/notifications/appointments`;

  // =============================================================================
  // STATE MANAGEMENT WITH SIGNALS
  // =============================================================================

  // Notification state signals
  private readonly unreadCountSignal = signal<number>(0);
  private readonly recentNotificationsSignal = signal<AppointmentNotificationResponse[]>([]);
  private readonly loadingSignal = signal<boolean>(false);
  private readonly errorSignal = signal<string | null>(null);
  private readonly pollingActiveSignal = signal<boolean>(false);

  // Public readonly signals
  readonly unreadCount = this.unreadCountSignal.asReadonly();
  readonly recentNotifications = this.recentNotificationsSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();
  readonly pollingActive = this.pollingActiveSignal.asReadonly();

  // Computed signals
  readonly hasUnread = computed(() => this.unreadCountSignal() > 0);
  readonly badgeText = computed(() => {
    const count = this.unreadCountSignal();
    return count > 99 ? '99+' : count.toString();
  });

  // Polling subscription
  private pollingSubscription: any = null;

  // =============================================================================
  // API METHODS
  // =============================================================================

  /**
   * Get notification summary for dropdown
   * الحصول على ملخص الإشعارات للقائمة المنسدلة
   */
  getNotificationSummary(): Observable<NotificationSummaryResponse> {
    return this.http.get<ApiResponse<NotificationSummaryResponse>>(`${this.apiUrl}/summary`).pipe(
      tap(response => {
        if (response.success && response.data) {
          this.unreadCountSignal.set(response.data.unreadCount);
          this.recentNotificationsSignal.set(response.data.recentNotifications);
        }
      }),
      map(response => response.data!),
      catchError(error => {
        console.error('Error fetching notification summary:', error);
        this.errorSignal.set('فشل في تحميل الإشعارات');
        return of({
          unreadCount: 0,
          recentNotifications: [],
          hasMore: false,
          totalCount: 0
        });
      })
    );
  }

  /**
   * Get unread count only (for badge polling)
   * الحصول على عدد الإشعارات غير المقروءة فقط
   */
  getUnreadCount(): Observable<number> {
    return this.http.get<ApiResponse<number>>(`${this.apiUrl}/unread-count`).pipe(
      tap(response => {
        if (response.success && response.data !== undefined) {
          this.unreadCountSignal.set(response.data);
        }
      }),
      map(response => response.data ?? 0),
      catchError(error => {
        console.error('Error fetching unread count:', error);
        return of(0);
      })
    );
  }

  /**
   * Get all notifications with pagination
   * الحصول على جميع الإشعارات مع الترقيم
   */
  getAllNotifications(page: number = 0, size: number = 20): Observable<NotificationPageResponse> {
    this.loadingSignal.set(true);

    return this.http.get<ApiResponse<NotificationPageResponse>>(
      `${this.apiUrl}?page=${page}&size=${size}`
    ).pipe(
      tap(() => {
        this.loadingSignal.set(false);
      }),
      map(response => response.data!),
      catchError(error => {
        this.loadingSignal.set(false);
        this.errorSignal.set('فشل في تحميل الإشعارات');
        console.error('Error fetching all notifications:', error);
        return of({
          notifications: [],
          totalElements: 0,
          totalPages: 0,
          currentPage: 0,
          pageSize: size,
          hasPrevious: false,
          hasNext: false
        });
      })
    );
  }

  /**
   * Get unread notifications only
   * الحصول على الإشعارات غير المقروءة فقط
   */
  getUnreadNotifications(): Observable<AppointmentNotificationResponse[]> {
    return this.http.get<ApiResponse<AppointmentNotificationResponse[]>>(`${this.apiUrl}/unread`).pipe(
      map(response => response.data ?? []),
      catchError(error => {
        console.error('Error fetching unread notifications:', error);
        return of([]);
      })
    );
  }

  /**
   * Get a single notification by ID
   * الحصول على إشعار محدد
   */
  getNotificationById(notificationId: number): Observable<AppointmentNotificationResponse | null> {
    return this.http.get<ApiResponse<AppointmentNotificationResponse>>(
      `${this.apiUrl}/${notificationId}`
    ).pipe(
      map(response => response.data ?? null),
      catchError(error => {
        console.error('Error fetching notification:', error);
        return of(null);
      })
    );
  }

  /**
   * Mark a single notification as read
   * تحديد إشعار كمقروء
   */
  markAsRead(notificationId: number): Observable<AppointmentNotificationResponse | null> {
    return this.http.patch<ApiResponse<AppointmentNotificationResponse>>(
      `${this.apiUrl}/${notificationId}/read`,
      {}
    ).pipe(
      tap(response => {
        if (response.success && response.data) {
          // Update local state
          const currentNotifications = this.recentNotificationsSignal();
          const updatedNotifications = currentNotifications.map(n =>
            n.id === notificationId ? { ...n, isRead: true } : n
          );
          this.recentNotificationsSignal.set(updatedNotifications);

          // Decrement unread count
          const currentCount = this.unreadCountSignal();
          if (currentCount > 0) {
            this.unreadCountSignal.set(currentCount - 1);
          }
        }
      }),
      map(response => response.data ?? null),
      catchError(error => {
        console.error('Error marking notification as read:', error);
        return of(null);
      })
    );
  }

  /**
   * Mark all notifications as read
   * تحديد جميع الإشعارات كمقروءة
   */
  markAllAsRead(): Observable<number> {
    return this.http.patch<ApiResponse<number>>(`${this.apiUrl}/read-all`, {}).pipe(
      tap(response => {
        if (response.success) {
          // Update local state
          const currentNotifications = this.recentNotificationsSignal();
          const updatedNotifications = currentNotifications.map(n => ({ ...n, isRead: true }));
          this.recentNotificationsSignal.set(updatedNotifications);
          this.unreadCountSignal.set(0);
        }
      }),
      map(response => response.data ?? 0),
      catchError(error => {
        console.error('Error marking all notifications as read:', error);
        return of(0);
      })
    );
  }

  /**
   * Mark specific notifications as read by IDs
   * تحديد إشعارات محددة كمقروءة
   */
  markAsReadByIds(notificationIds: number[]): Observable<number> {
    return this.http.patch<ApiResponse<number>>(`${this.apiUrl}/read`, notificationIds).pipe(
      tap(response => {
        if (response.success && response.data) {
          // Update local state
          const currentNotifications = this.recentNotificationsSignal();
          const updatedNotifications = currentNotifications.map(n =>
            notificationIds.includes(n.id) ? { ...n, isRead: true } : n
          );
          this.recentNotificationsSignal.set(updatedNotifications);

          // Update unread count
          const markedCount = response.data;
          const currentCount = this.unreadCountSignal();
          this.unreadCountSignal.set(Math.max(0, currentCount - markedCount));
        }
      }),
      map(response => response.data ?? 0),
      catchError(error => {
        console.error('Error marking notifications as read:', error);
        return of(0);
      })
    );
  }

  /**
   * Delete a notification
   * حذف إشعار
   */
  deleteNotification(notificationId: number): Observable<boolean> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${notificationId}`).pipe(
      tap(response => {
        if (response.success) {
          // Remove from local state
          const currentNotifications = this.recentNotificationsSignal();
          const notification = currentNotifications.find(n => n.id === notificationId);

          const updatedNotifications = currentNotifications.filter(n => n.id !== notificationId);
          this.recentNotificationsSignal.set(updatedNotifications);

          // Update unread count if the deleted notification was unread
          if (notification && !notification.isRead) {
            const currentCount = this.unreadCountSignal();
            if (currentCount > 0) {
              this.unreadCountSignal.set(currentCount - 1);
            }
          }
        }
      }),
      map(response => response.success),
      catchError(error => {
        console.error('Error deleting notification:', error);
        return of(false);
      })
    );
  }

  // =============================================================================
  // POLLING METHODS
  // =============================================================================

  /**
   * Start polling for new notifications
   * بدء الاستطلاع للإشعارات الجديدة
   */
  startPolling(intervalMs: number = 30000): void {
    if (this.pollingSubscription) {
      return; // Already polling
    }

    this.pollingActiveSignal.set(true);

    // Initial fetch
    this.getNotificationSummary().subscribe();

    // Start polling
    this.pollingSubscription = interval(intervalMs).pipe(
      switchMap(() => this.getUnreadCount())
    ).subscribe();
  }

  /**
   * Stop polling for notifications
   * إيقاف الاستطلاع
   */
  stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
    this.pollingActiveSignal.set(false);
  }

  /**
   * Refresh notifications manually
   * تحديث الإشعارات يدوياً
   */
  refresh(): void {
    this.getNotificationSummary().subscribe();
  }

  /**
   * Clear local notification state (on logout)
   * مسح حالة الإشعارات المحلية
   */
  clearState(): void {
    this.stopPolling();
    this.unreadCountSignal.set(0);
    this.recentNotificationsSignal.set([]);
    this.errorSignal.set(null);
  }
}
